import React, { useState, useRef, useEffect } from 'react'
import { FaCrop, FaAdjust, FaFilter, FaPaintBrush, FaFont, FaUndo, FaRedo, FaTimes, FaSave, FaExpand, FaCompress } from 'react-icons/fa'
import CropTool from './CropTool'
import AdjustmentTools from './AdjustmentTools'
import FilterTools from './FilterTools'
import DrawingTools from './DrawingTools'
import TextTools from './TextTools'

const PhotoEditor = ({ image, onSave, onCancel }) => {
  const [activeTab, setActiveTab] = useState('crop')
  const [editedImage, setEditedImage] = useState(image)
  const [history, setHistory] = useState([image])
  const [historyIndex, setHistoryIndex] = useState(0)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [editorHeight, setEditorHeight] = useState(85)
  const [isResizing, setIsResizing] = useState(false)
  const [editorWidth] = useState(95)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const editorRef = useRef(null)
  const startYRef = useRef(0)
  const startHeightRef = useRef(85)
  const dragStartRef = useRef({ x: 0, y: 0 })

  const addToHistory = (newImage) => {
    const newHistory = history.slice(0, historyIndex + 1)
    newHistory.push(newImage)
    setHistory(newHistory)
    setHistoryIndex(newHistory.length - 1)
    setEditedImage(newImage)
  }

  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1)
      setEditedImage(history[historyIndex - 1])
    }
  }

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1)
      setEditedImage(history[historyIndex + 1])
    }
  }

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen)
  }

  const handleResizeStart = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsResizing(true)
    startYRef.current = e.clientY
    startHeightRef.current = editorHeight
  }

  const handleDragStart = (e) => {
    if (isFullscreen) return
    e.preventDefault()
    setIsDragging(true)
    dragStartRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    }
  }

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizing) {
        e.preventDefault()
        const deltaY = e.clientY - startYRef.current
        const viewportHeight = window.innerHeight
        const deltaPercent = (deltaY / viewportHeight) * 100
        const newHeight = startHeightRef.current + deltaPercent

        if (newHeight >= 40 && newHeight <= 95) {
          setEditorHeight(newHeight)
        }
      } else if (isDragging) {
        e.preventDefault()
        setPosition({
          x: e.clientX - dragStartRef.current.x,
          y: e.clientY - dragStartRef.current.y
        })
      }
    }

    const handleMouseUp = () => {
      if (isResizing) {
        setIsResizing(false)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
      if (isDragging) {
        setIsDragging(false)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }

    if (isResizing || isDragging) {
      document.body.style.cursor = isResizing ? 'ns-resize' : 'move'
      document.body.style.userSelect = 'none'
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing, isDragging, position])

  const tabs = [
    { id: 'crop', name: 'Crop', icon: FaCrop },
    { id: 'adjust', name: 'Adjust', icon: FaAdjust },
    { id: 'filter', name: 'Filter', icon: FaFilter },
    { id: 'draw', name: 'Draw', icon: FaPaintBrush },
    { id: 'text', name: 'Text', icon: FaFont }
  ]

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-[100] flex items-center justify-center p-4">
      <div
        ref={editorRef}
        className="bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden"
        style={{
          width: isFullscreen ? '98vw' : `${editorWidth}vw`,
          height: isFullscreen ? '98vh' : `${editorHeight}vh`,
          maxWidth: isFullscreen ? 'none' : '1800px',
          transform: isFullscreen ? 'none' : `translate(${position.x}px, ${position.y}px)`,
          position: isFullscreen ? 'static' : 'relative'
        }}
      >
        <div className="bg-white shadow-lg flex-shrink-0">
          <div
            className="px-4 py-3 flex items-center justify-between cursor-move"
            onMouseDown={handleDragStart}
          >
            <h2 className="text-xl font-bold text-gray-800">Photo Editor</h2>

            <div className="flex items-center gap-2">
              <button
                onClick={handleUndo}
                disabled={historyIndex === 0}
                className="p-2 rounded-lg bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                title="Undo"
              >
                <FaUndo className="text-gray-700" />
              </button>
              <button
                onClick={handleRedo}
                disabled={historyIndex === history.length - 1}
                className="p-2 rounded-lg bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors"
                title="Redo"
              >
                <FaRedo className="text-gray-700" />
              </button>
              <button
                onClick={toggleFullscreen}
                className="p-2 rounded-lg bg-gray-200 hover:bg-gray-300 transition-colors"
                title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
              >
                {isFullscreen ? <FaCompress className="text-gray-700" /> : <FaExpand className="text-gray-700" />}
              </button>
              <button
                onClick={() => onSave(editedImage)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
              >
                <FaSave />
                Save
              </button>
              <button
                onClick={onCancel}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
              >
                <FaTimes />
                Cancel
              </button>
            </div>
          </div>

          <div className="border-t border-gray-200">
            <div className="px-4">
              <div className="flex gap-1 overflow-x-auto">
                {tabs.map(tab => {
                  const Icon = tab.icon
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors whitespace-nowrap ${
                        activeTab === tab.id
                          ? 'text-blue-600 border-b-2 border-blue-600'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                    >
                      <Icon />
                      {tab.name}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        {!isFullscreen && (
          <div
            onMouseDown={handleResizeStart}
            className="h-3 bg-gradient-to-b from-gray-300 to-gray-400 hover:from-blue-400 hover:to-blue-500 cursor-ns-resize transition-all flex-shrink-0 relative group"
            title="Drag to resize editor"
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-12 h-1 bg-white/50 rounded-full" />
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto bg-gray-50">
          <div className="px-6 py-6 h-full">
            {activeTab === 'crop' && (
              <CropTool
                image={editedImage}
                onCropComplete={addToHistory}
              />
            )}
            {activeTab === 'adjust' && (
              <AdjustmentTools
                image={editedImage}
                onAdjust={addToHistory}
              />
            )}
            {activeTab === 'filter' && (
              <FilterTools
                image={editedImage}
                onFilterApply={addToHistory}
              />
            )}
            {activeTab === 'draw' && (
              <DrawingTools
                image={editedImage}
                onDrawingComplete={addToHistory}
              />
            )}
            {activeTab === 'text' && (
              <TextTools
                image={editedImage}
                onTextComplete={addToHistory}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PhotoEditor
